import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Upload, X, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AvatarUploadOptions } from '@/services/profile/types';
import { DEFAULT_AVATAR_OPTIONS } from '@/services/profile/types';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  isUploading?: boolean;
  error?: string;
  options?: AvatarUploadOptions;
  className?: string;
  disabled?: boolean;
  userInitials?: string;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  onFileSelect,
  onRemove,
  isUploading = false,
  error,
  options = DEFAULT_AVATAR_OPTIONS,
  className,
  disabled = false,
  userInitials = 'U',
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    try {
      // Validate file
      if (file.size > (options.maxSizeBytes || DEFAULT_AVATAR_OPTIONS.maxSizeBytes!)) {
        throw new Error(`File size must be less than ${Math.round((options.maxSizeBytes || DEFAULT_AVATAR_OPTIONS.maxSizeBytes!) / 1024 / 1024)}MB`);
      }

      const allowedTypes = options.allowedTypes || DEFAULT_AVATAR_OPTIONS.allowedTypes!;
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type must be one of: ${allowedTypes.join(', ')}`);
      }

      // Create preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Call parent handler
      onFileSelect(file);
    } catch (err) {
      console.error('File validation failed:', err);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled || isUploading) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove?.();
  };

  const displayUrl = previewUrl || currentAvatarUrl;
  const showRemoveButton = (displayUrl || previewUrl) && onRemove && !disabled && !isUploading;

  return (
    <div className={cn('flex flex-col items-center space-y-4', className)}>
      {/* Avatar Display */}
      <div className="relative">
        <Avatar className="h-24 w-24">
          <AvatarImage src={displayUrl} alt="Profile picture" />
          <AvatarFallback className="text-lg">
            {userInitials}
          </AvatarFallback>
        </Avatar>
        
        {showRemoveButton && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
            onClick={handleRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
        
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-gray-300',
          disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary hover:bg-primary/5',
          error ? 'border-red-300 bg-red-50' : ''
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 mb-1">
          {dragOver ? 'Drop image here' : 'Click to upload or drag and drop'}
        </p>
        <p className="text-xs text-gray-500">
          {options.allowedTypes?.map(type => type.split('/')[1].toUpperCase()).join(', ')} up to{' '}
          {Math.round((options.maxSizeBytes || DEFAULT_AVATAR_OPTIONS.maxSizeBytes!) / 1024 / 1024)}MB
        </p>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={options.allowedTypes?.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      {/* Upload Button (Alternative) */}
      {!displayUrl && (
        <Button
          type="button"
          variant="outline"
          onClick={handleClick}
          disabled={disabled || isUploading}
          className="w-full"
        >
          <User className="mr-2 h-4 w-4" />
          Choose Profile Picture
        </Button>
      )}
    </div>
  );
};
